import React, { useState } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import {
  Text,
  TextInput,
  useTheme,
  IconButton,
  Card,
} from 'react-native-paper';
import { useFormContext } from 'react-hook-form';
import {
  Plus,
  ExternalLink,
  Trash2,
  ShoppingCart,
  Globe,
  Coffee,
  Link,
} from 'lucide-react-native';

const popularPlatforms = [
  { name: 'Amazon', icon: ShoppingCart },
  { name: 'Official Website', icon: Globe },
  { name: 'Specialty Retailer', icon: Coffee },
  { name: 'Other', icon: Link },
];

export function EquipmentAffiliateLinks() {
  const theme = useTheme();
  const colors = theme.colors;
  const isDark = theme.dark;
  const { watch, setValue } = useFormContext();

  const affiliateLinks = watch('affiliate_links') || {};

  const [newPlatform, setNewPlatform] = useState('');
  const [newLink, setNewLink] = useState('');

  const addLink = () => {
    if (newPlatform.trim() && newLink.trim()) {
      setValue('affiliate_links', {
        ...affiliateLinks,
        [newPlatform.trim()]: newLink.trim(),
      });
      setNewPlatform('');
      setNewLink('');
    }
  };

  const removeLink = (platform: string) => {
    const updated = { ...affiliateLinks };
    delete updated[platform];
    setValue('affiliate_links', updated);
  };

  const addQuickLink = (platformName: string) => {
    setNewPlatform(platformName);
  };

  return (
    <View style={styles.container}>
      {/* Quick Add Platforms */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Quick Add Platform
        </Text>
        <View style={styles.platformGrid}>
          {popularPlatforms.map(platform => {
            const IconComponent = platform.icon;
            return (
              <Pressable
                key={platform.name}
                onPress={() => addQuickLink(platform.name)}
                style={[
                  styles.platformCard,
                  {
                    backgroundColor:
                      newPlatform === platform.name
                        ? isDark
                          ? 'rgba(103, 80, 164, 0.15)'
                          : 'rgba(103, 80, 164, 0.08)'
                        : isDark
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(0, 0, 0, 0.02)',
                    borderColor:
                      newPlatform === platform.name
                        ? colors.primary
                        : isDark
                          ? 'rgba(255, 255, 255, 0.12)'
                          : 'rgba(0, 0, 0, 0.12)',
                  },
                ]}
              >
                <IconComponent
                  size={28}
                  color={
                    newPlatform === platform.name
                      ? colors.primary
                      : colors.onSurfaceVariant
                  }
                  style={{ marginBottom: 6 }}
                />
                <Text
                  variant='labelSmall'
                  style={{
                    color:
                      newPlatform === platform.name
                        ? colors.primary
                        : colors.onSurface,
                    textAlign: 'center',
                  }}
                >
                  {platform.name}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>

      {/* Add New Link */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Add Affiliate Link
        </Text>

        <View style={styles.addLinkContainer}>
          <View style={{ flex: 1 }}>
            <Text
              variant='labelSmall'
              style={{ color: colors.onSurfaceVariant, marginBottom: 6 }}
            >
              Platform / Retailer
            </Text>
            <TextInput
              mode='outlined'
              placeholder='e.g., Amazon, Official Store'
              value={newPlatform}
              onChangeText={setNewPlatform}
              style={styles.input}
              outlineStyle={styles.inputOutline}
            />
          </View>
        </View>

        <View style={styles.addLinkContainer}>
          <View style={{ flex: 1 }}>
            <Text
              variant='labelSmall'
              style={{ color: colors.onSurfaceVariant, marginBottom: 6 }}
            >
              Affiliate URL
            </Text>
            <TextInput
              mode='outlined'
              placeholder='https://...'
              value={newLink}
              onChangeText={setNewLink}
              style={styles.input}
              outlineStyle={styles.inputOutline}
              autoCapitalize='none'
              autoCorrect={false}
              keyboardType='url'
            />
          </View>
        </View>

        <Pressable
          onPress={addLink}
          style={[
            styles.addButton,
            {
              backgroundColor: colors.primary,
              opacity: newPlatform.trim() && newLink.trim() ? 1 : 0.5,
            },
          ]}
          disabled={!newPlatform.trim() || !newLink.trim()}
        >
          <Plus size={20} color={colors.onPrimary} />
          <Text variant='labelMedium' style={{ color: colors.onPrimary }}>
            Add Link
          </Text>
        </Pressable>
      </View>

      {/* Existing Links */}
      {Object.keys(affiliateLinks).length > 0 && (
        <View style={styles.fieldContainer}>
          <Text
            variant='labelLarge'
            style={[styles.label, { color: colors.onSurface }]}
          >
            Affiliate Links ({Object.keys(affiliateLinks).length})
          </Text>

          {Object.entries(affiliateLinks).map(([platform, url]) => (
            <View
              key={platform}
              style={[
                styles.linkCard,
                {
                  backgroundColor: isDark
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.02)',
                  borderColor: isDark
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.08)',
                },
              ]}
            >
              <View style={{ flex: 1 }}>
                <View style={styles.linkHeader}>
                  <Text
                    variant='labelMedium'
                    style={{ color: colors.onSurface }}
                  >
                    {platform}
                  </Text>
                  <ExternalLink size={14} color={colors.primary} />
                </View>
                <Text
                  variant='bodySmall'
                  style={{ color: colors.onSurfaceVariant, marginTop: 4 }}
                  numberOfLines={1}
                >
                  {url as string}
                </Text>
              </View>
              <IconButton
                icon={() => <Trash2 size={16} color={colors.error} />}
                onPress={() => removeLink(platform)}
                size={16}
              />
            </View>
          ))}
        </View>
      )}

      {/* Empty State */}
      {Object.keys(affiliateLinks).length === 0 && (
        <View
          style={[
            styles.emptyState,
            {
              backgroundColor: isDark
                ? 'rgba(255, 255, 255, 0.03)'
                : 'rgba(0, 0, 0, 0.01)',
              borderColor: isDark
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.06)',
            },
          ]}
        >
          <Link
            size={48}
            color={colors.onSurfaceVariant}
            style={{ marginBottom: 8 }}
          />
          <Text
            variant='titleMedium'
            style={{ color: colors.onSurface, marginBottom: 4 }}
          >
            No affiliate links yet
          </Text>
          <Text
            variant='bodySmall'
            style={{ color: colors.onSurfaceVariant, textAlign: 'center' }}
          >
            Add affiliate links to help users purchase this equipment
          </Text>
        </View>
      )}

      {/* Best Practices */}
      <Card
        style={[
          styles.card,
          styles.tipsCard,
          { backgroundColor: theme.colors.primaryContainer },
        ]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={[
              styles.tipsTitle,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            Tips for Affiliate Links
          </Text>
          <Text
            variant='bodySmall'
            style={[
              styles.tipsText,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            • Use descriptive platform names{'\n'}• Verify links before adding
            {'\n'}• Include multiple retailers for user choice{'\n'}• Keep URLs
            up to date{'\n'}• Add affiliate links to help users find and
            purchase equipment
          </Text>
        </Card.Content>
      </Card>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 24,
  },
  fieldContainer: {
    gap: 8,
  },
  label: {
    fontWeight: '600',
  },
  input: {
    fontSize: 15,
  },
  inputOutline: {
    borderRadius: 12,
  },
  platformGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 8,
  },
  platformCard: {
    flex: 1,
    minWidth: '22%',
    aspectRatio: 1,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addLinkContainer: {
    marginTop: 8,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 14,
    borderRadius: 12,
    marginTop: 12,
  },
  linkCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 12,
    borderWidth: 1,
    marginTop: 8,
  },
  linkHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    borderRadius: 16,
    borderWidth: 2,
    borderStyle: 'dashed',
  },
  card: {
    marginTop: 8,
  },
  tipsCard: {
    marginTop: 8,
  },
  tipsTitle: {
    marginBottom: 8,
    fontWeight: 'bold',
  },
  tipsText: {
    lineHeight: 20,
  },
});
